/**
 * Who an iOOH deck is prepared for and which campaign it measures. Pure.
 *
 * BT-2070 (Vardan Kirakosyan, 2026-10-01): the planner's campaign step is asked as Client Name, the
 * account the project is tied to. Slide 1's Prepared for prints the client, and slide 4 reads
 * "<Client> would like to measure the effectiveness of the <Brand> campaign." The step's id stays
 * `campaign_name`: only its question is renamed, in the iOOH report template's row
 * (`atlas.pdlc_report_templates`, /tools/banner-plan-templates) and in DANI's own iOOH row. Both
 * study builders read the answer through `clientNameFromAnswers`, which tries a `client_name` step
 * first, in case the id is renamed too.
 *
 * Before BT-2070 the step was the campaign name (M4S-5626, BT-2053): slide 1 printed the brand and
 * slide 4 read "<Brand> would like to measure the effectiveness of the <Campaign> campaign." A deck
 * re-renders from its stored study (`iooh-study-<conv>` and saved reports), and one built then stores
 * a `campaignName` and no `clientName`, so it keeps that reading here, at render time, instead of
 * being migrated.
 */

import type { IoohStudyDetails } from './iooh-slide-schema';

/** A planner answer as a name: trimmed, and '' for nothing, a non-string or `__auto__`. */
export function cleanIntakeName(raw: unknown): string {
    if (typeof raw !== 'string') return '';
    const v = raw.trim();
    return v === '__auto__' ? '' : v;
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The planner steps the client is read from, first answered wins (BT-2070). */
const CLIENT_STEP_IDS = ['client_name', 'client', 'campaign_name', 'campaign'] as const;

/**
 * The client from the planner's answers, as both study builders store it (BT-2070): a `client_name`
 * or `client` step if the template has one, else the `campaign_name` step that asks for the client.
 */
export function clientNameFromAnswers(
    answers: Readonly<Record<string, unknown>> | null | undefined,
): string {
    for (const id of CLIENT_STEP_IDS) {
        const name = cleanIntakeName(answers?.[id]);
        if (name) return name;
    }
    return '';
}

/**
 * The client the researcher typed, or '' for none (and on a deck built before BT-2070). The old
 * "<brand> Brand Lift Report" stand-in is not a client (BT-2053), compared without case.
 */
export function resolveClientName(
    study: Pick<IoohStudyDetails, 'clientName' | 'brandName'>,
): string {
    const name = cleanIntakeName(study.clientName);
    const brand = (study.brandName ?? '').trim();
    if (brand && sameName(name, `${brand} Brand Lift Report`)) return '';
    return name;
}

/**
 * A deck built before BT-2070: the campaign its researcher typed, or '' when the study has none:
 * blank, the brand itself, or "<brand> Brand Lift Report" (the old pre-fill), compared without case.
 */
export function resolveCampaignName(
    study: Pick<IoohStudyDetails, 'campaignName' | 'brandName'>,
): string {
    const name = cleanIntakeName(study.campaignName);
    if (!name) return '';
    const brand = (study.brandName ?? '').trim().toLowerCase();
    const n = name.toLowerCase();
    if (brand && (n === brand || n === `${brand} brand lift report`)) return '';
    return name;
}

/** The campaign slides 4 and 11 name. */
export type IoohCampaign =
    /** The brand's: the client measures the brand's campaign (BT-2070). */
    | { kind: 'brand'; name: string }
    /** A deck built before BT-2070: the campaign its researcher typed (BT-2053). */
    | { kind: 'legacy'; name: string }
    /** None to name: "its out-of-home campaign" on slide 4, "the campaign" on slide 11. */
    | { kind: 'none' };

export interface IoohDeckNames {
    /** Slide 1's Prepared for: the client, or the brand when the deck has none. */
    preparedFor: string;
    /** Who "would like to measure" on slide 4: the client, or the brand when the deck has none. */
    measuredBy: string;
    campaign: IoohCampaign;
}

/**
 * The names the deck prints. A client who is the brand itself reads "<Client> would like to measure
 * the effectiveness of its out-of-home campaign.", not "the <Client> campaign" after its own name.
 */
export function ioohDeckNames(
    study: Pick<IoohStudyDetails, 'brandName' | 'clientName' | 'campaignName'>,
): IoohDeckNames {
    const brand = study.brandName ?? '';
    const client = resolveClientName(study);
    if (client) {
        const campaign: IoohCampaign =
            brand.trim() && !sameName(client, brand)
                ? { kind: 'brand', name: brand }
                : { kind: 'none' };
        return { preparedFor: client, measuredBy: client, campaign };
    }
    const legacy = resolveCampaignName(study);
    return {
        preparedFor: brand,
        measuredBy: brand,
        campaign: legacy ? { kind: 'legacy', name: legacy } : { kind: 'none' },
    };
}
